import React, { useState, useEffect, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { UsersTable, UserDetailsDrawer, CreateUserDrawer, EditUserDrawer, type User } from '../components/users';
import { MemberDetailsDrawer } from '../components/memberships/MemberDetailsDrawer';
import type { Membership } from '../components/memberships/MembershipsTable';
import { MembershipsService } from '../services/memberships/memberships.service';
import { UsersService } from '../services/users/users.service';
import type { PaginationMeta, PaginatedUsersResponse } from '../services/users/users.service';
import { NotificationToast } from '../components/ui/NotificationToast';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../store/auth.store';

export const UsuariosPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { user: storeUser } = useAuthStore();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(7);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [tipoUsuario, setTipoUsuario] = useState<'administrador' | 'miembro' | undefined>(undefined);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isDrawerOpen = selectedUser !== null;
  
  // Verificar permisos
  const canCreateUser = hasPermission('Usuarios', 'crear');
  const canViewUsers = hasPermission('Usuarios', 'ver');
  const canEditUsers = hasPermission('Usuarios', 'editar');
  const canDeleteUsers = hasPermission('Usuarios', 'eliminar');
  const canAdministerUsers = hasPermission('Usuarios', 'administrar');
  
  
  // Verificar si el usuario tiene al menos permisos básicos para ver la página
  const hasBasicAccess = canViewUsers || canAdministerUsers;
  
  // Estados para notificaciones de actualización
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [updateNotificationConfig, setUpdateNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  
  // Cerrar drawer cuando se desmonta el componente (navegación)
  useEffect(() => {
    return () => {
      setSelectedUser(null);
    };
  }, []);

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms de delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Resetear página cuando cambie el término de búsqueda debounced
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, tipoUsuario]);

  // Cerrar dropdown cuando se hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const { 
    data, 
    isLoading, 
    error,
    refetch,
    isFetching
  } = useQuery<PaginatedUsersResponse>({
    queryKey: ['users', page, limit, debouncedSearchTerm, tipoUsuario],
    queryFn: () => {
      if (debouncedSearchTerm.trim()) {
        return UsersService.searchUsers(debouncedSearchTerm, page, limit, tipoUsuario);
      }
      return UsersService.getUsers(page, limit, tipoUsuario);
    },
    placeholderData: keepPreviousData,
  });

  const users = data?.data || [];
  const paginationMeta = data?.meta;

  // Validación adicional para asegurar que users sea siempre un array
  const safeUsers = Array.isArray(users) ? users : [];

  // Estado para mostrar loading solo en el input mientras se escribe (antes del debounce)
  const isSearching = isFetching && !!debouncedSearchTerm;

  const handleUserClick = (user: User) => {
    setSelectedUser(user);
  };

  const handleCloseDrawer = () => {
    setSelectedUser(null);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePreviousPage = () => {
    if (paginationMeta?.hasPrevious) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationMeta?.hasNext) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Resetear a la primera página cuando cambie el límite
  };

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const handleTipoUsuarioChange = (tipo: 'administrador' | 'miembro' | undefined) => {
    setTipoUsuario(tipo);
  };

  const handleCreateUserSuccess = () => {
    refetch();
  };

  const handleEditUser = (user: User) => {
    setUserToEdit(user);
  };

  const handleCloseEditDrawer = () => {
    setUserToEdit(null);
  };

  const handleEditUserSuccess = () => {
    setUserToEdit(null);
    refetch();
    // Actualizar también el usuario seleccionado si es el mismo que se editó
    if (selectedUser && userToEdit && selectedUser.id === userToEdit.id) {
      // Volver a cargar los datos del usuario seleccionado
      setSelectedUser(null);
    }
  };

  const handleEditUserResult = (success: boolean, message: string) => {
    setUpdateNotificationConfig({
      title: success ? '¡Usuario actualizado!' : 'Error al actualizar',
      message: message,
      type: success ? 'success' : 'error'
    });
    setShowUpdateNotification(true);
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      const updatedUser = await UsersService.toggleUserStatus(user.id);
      refetch();
      // Actualizar el usuario seleccionado con los datos actualizados
      // en lugar de cerrar el drawer inmediatamente
      if (selectedUser && selectedUser.id === user.id) {
        setSelectedUser(updatedUser);
      }
    } catch (error) {
      console.error('Error al cambiar estado del usuario:', error);
      throw error; // Re-throw para que el toast lo maneje
    }
  };

  // Abrir el drawer de detalle de membresía asociado al usuario
  const [memberToView, setMemberToView] = useState<Membership | null>(null);

  const handleViewMember = async (user: User) => {
    if (!user.membresiaId) return;
    try {
      const membership = await MembershipsService.getMembership(user.membresiaId);
      setMemberToView(membership);
    } catch (error) {
      console.error('Error al cargar la membresía del usuario:', error);
      setUpdateNotificationConfig({
        title: 'Error al cargar el miembro',
        message: 'No se pudo cargar la información de membresía de este usuario.',
        type: 'error'
      });
      setShowUpdateNotification(true);
    }
  };


  // Verificar acceso básico primero
  if (!hasBasicAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <svg className="mx-auto h-16 w-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h1 className="text-xs font-medium text-gray-900 mb-2">Acceso denegado</h1>
          <p className="text-gray-600 mb-4">
            No tienes permisos para acceder a la gestión de usuarios.
          </p>
          <p className="text-xs text-gray-500">
            Necesitas al menos el permiso "ver" o "administrar" en el módulo de Usuarios.
          </p>
        </div>
      </div>
    );
  }

  // Loading inicial: solo muestra spinner de pantalla completa en la primera carga real
  // (cuando no hay datos previos ni placeholder)
  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xs mb-4">Error al cargar los usuarios</div>
          <button 
            onClick={() => refetch()}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
        {/* Filtros y barra de búsqueda */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
          {/* Filtros de tipo de usuario */}
          <div className="flex items-center space-x-2 flex-wrap gap-y-2">
            <span className="hidden sm:inline text-xs text-gray-500 font-medium">Filtrar:</span>
            <div className="flex space-x-1">
              <button
                onClick={() => handleTipoUsuarioChange(undefined)}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-xs rounded-lg transition-colors ${
                  tipoUsuario === undefined
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => handleTipoUsuarioChange('administrador')}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-xs rounded-lg transition-colors ${
                  tipoUsuario === 'administrador'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="sm:hidden">Admins</span>
                <span className="hidden sm:inline">Administradores</span>
              </button>
              <button
                onClick={() => handleTipoUsuarioChange('miembro')}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-xs rounded-lg transition-colors ${
                  tipoUsuario === 'miembro'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Miembros
              </button>
            </div>
          </div>
          
          <div className="relative flex-1 w-full sm:max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {isFetching && searchTerm ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
              ) : (
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar por nombre, email..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-xs placeholder-gray-500 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-2 md:space-x-3 flex-wrap gap-y-2">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200/50 rounded-lg px-2.5 sm:px-4 py-2">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                <div className="text-xs sm:text-xs">
                  {searchTerm ? (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-purple-700">{paginationMeta ? paginationMeta.total : safeUsers.length}</span>
                      <span className="hidden sm:inline text-gray-600">resultados</span>
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-purple-700">{paginationMeta ? paginationMeta.total : safeUsers.length}</span>
                      <span className="hidden sm:inline text-gray-600">usuarios</span>
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Selector de cantidad por página */}
            <div className="flex items-center space-x-1.5 sm:space-x-2">
              <span className="hidden sm:inline text-xs text-gray-500 font-medium">Mostrar</span>
              <div className="relative" data-dropdown>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="bg-white border border-gray-300 rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2 pr-7 sm:pr-8 text-xs sm:text-xs font-medium text-gray-700 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all cursor-pointer flex items-center min-w-[3rem]"
                >
                  {limit}
                </button>
                <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 sm:pr-2 pointer-events-none">
                  <svg className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                    {[10, 20, 30, 50, 100].map((value) => (
                      <div
                        key={value}
                        onClick={() => {
                          handleLimitChange(value);
                          setIsDropdownOpen(false);
                        }}
                        className="px-3 py-2 text-xs font-medium text-purple-700 hover:bg-purple-50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        {value}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {canCreateUser && (
              <button
                onClick={() => setIsCreateUserOpen(true)}
                className="bg-purple-600 text-white px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-1 sm:space-x-2"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="text-xs sm:text-xs">Nuevo</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabla de usuarios */}
      <UsersTable 
        users={safeUsers}
        onUserClick={handleUserClick}
        isLoading={isLoading && safeUsers.length === 0}
      />

      {/* Paginación */}
      {paginationMeta && (
        <div className="px-4 lg:px-6 py-4 lg:py-6 border-t border-gray-100 bg-gray-50/30">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Información de resultados */}
            <div className="flex items-center space-x-4 md:space-x-6">
              <div className="text-xs md:text-xs text-gray-600">
                <span className="font-medium text-gray-900">
                  {((paginationMeta.page - 1) * paginationMeta.limit) + 1}
                </span>
                <span className="mx-1">–</span>
                <span className="font-medium text-gray-900">
                  {Math.min(paginationMeta.page * paginationMeta.limit, paginationMeta.total)}
                </span>
                <span className="mx-1 hidden sm:inline">de</span>
                <span className="hidden sm:inline font-medium text-gray-900">{paginationMeta.total}</span>
                <span className="ml-1 hidden sm:inline">resultados</span>
              </div>
              
              {/* Páginas X de Y */}
              <div className="hidden md:block text-xs text-gray-500">
                Página <span className="font-medium text-gray-900">{paginationMeta.page}</span> de <span className="font-medium text-gray-900">{paginationMeta.totalPages}</span>
              </div>
            </div>
            
            {/* Controles de paginación */}
            <div className="flex items-center space-x-1">
              {/* Botón primera página */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={!paginationMeta.hasPrevious}
                className={`hidden sm:flex w-9 h-9 items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Primera página"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              {/* Botón página anterior */}
              <button
                onClick={handlePreviousPage}
                disabled={!paginationMeta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página anterior"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Páginas centrales */}
              <div className="flex items-center space-x-1 mx-1 md:mx-2">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : 3;
                  let start = Math.max(1, paginationMeta.page - Math.floor(maxVisiblePages / 2));
                  let end = Math.min(paginationMeta.totalPages, start + maxVisiblePages - 1);
                  
                  start = Math.max(1, end - maxVisiblePages + 1);

                  // Mostrar primera página si no está en el rango
                  if (start > 1) {
                    pages.push(
                      <button
                        key="1"
                        onClick={() => handlePageChange(1)}
                        className="hidden sm:flex w-9 h-9 items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        1
                      </button>
                    );
                    if (start > 2) {
                      pages.push(
                        <span key="ellipsis-start" className="hidden sm:flex w-9 h-9 items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                  }

                  // Páginas del rango central
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`w-9 h-9 flex items-center justify-center text-xs font-medium rounded-lg transition-all ${
                          i === paginationMeta.page
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Mostrar última página si no está en el rango
                  if (end < paginationMeta.totalPages) {
                    if (end < paginationMeta.totalPages - 1) {
                      pages.push(
                        <span key="ellipsis-end" className="hidden sm:flex w-9 h-9 items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                    pages.push(
                      <button
                        key={paginationMeta.totalPages}
                        onClick={() => handlePageChange(paginationMeta.totalPages)}
                        className="hidden sm:flex w-9 h-9 items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        {paginationMeta.totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              {/* Botón página siguiente */}
              <button
                onClick={handleNextPage}
                disabled={!paginationMeta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página siguiente"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Botón última página */}
              <button
                onClick={() => handlePageChange(paginationMeta.totalPages)}
                disabled={!paginationMeta.hasNext}
                className={`hidden sm:flex w-9 h-9 items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Última página"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer de detalles del usuario */}
      <UserDetailsDrawer 
        isOpen={isDrawerOpen}
        user={selectedUser}
        onClose={handleCloseDrawer}
        onEditUser={handleEditUser}
        onToggleUserStatus={handleToggleUserStatus}
        onViewMember={handleViewMember}
      />

      {/* Drawer de detalle de membresía (se abre desde el detalle de usuario) */}
      <MemberDetailsDrawer
        isOpen={memberToView !== null}
        member={memberToView}
        onClose={() => setMemberToView(null)}
      />

      {/* Drawer de crear usuario */}
      <CreateUserDrawer 
        isOpen={isCreateUserOpen}
        onClose={() => setIsCreateUserOpen(false)}
        onSuccess={handleCreateUserSuccess}
      />

      {/* Drawer de editar usuario */}
      <EditUserDrawer 
        isOpen={userToEdit !== null}
        user={userToEdit}
        onClose={handleCloseEditDrawer}
        onSuccess={handleEditUserSuccess}
        onUpdateResult={handleEditUserResult}
      />
      
      {/* Notification Toast para actualización de usuarios */}
      {updateNotificationConfig && (
        <NotificationToast
          isOpen={showUpdateNotification}
          title={updateNotificationConfig.title}
          message={updateNotificationConfig.message}
          type={updateNotificationConfig.type}
          onClose={() => {
            setShowUpdateNotification(false);
            setUpdateNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default UsuariosPage;